import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CenteredLoadingSpinner from '../../components/CenteredLoadingSpinner';
import type { FinancialDateMode } from '../../data/mockStockData';
import {
  fetchAnalystEstimates,
  fetchCashFlowStatement,
  fetchCashFlowStatementTTM,
  fetchIncomeStatement,
  fetchIncomeStatementTTM,
  fetchKeyMetrics,
  AnalystEstimate,
  CashFlowStatement,
  IncomeStatement,
  KeyMetrics,
} from '../../services/stockApi';
import { fetchFiscalCashFlowStatement, fetchFiscalIncomeStatement, fetchFiscalRatios } from '../../services/fiscalApi';

export type ResearchGrowthTablesMode = 'page' | 'embedded';

interface ResearchGrowthTablesProps {
  isDarkMode: boolean;
  selectedStock: string;
  selectedPeriod: 'annual' | 'quarter';
  mode?: ResearchGrowthTablesMode;
  asOfDate?: string | Date;
  rangeStart?: string | Date;
  selectedPeriodType?: 'Annual Data' | 'Quarterly Data' | 'TTM';
  analysisDateMode?: FinancialDateMode;
  financialsSource?: 'fmp' | 'fiscal';
  sections?: 'all' | 'calc-analyst' | 'analyst-only' | 'analyst-panel';
  useFrame?: boolean;
  calcTitleSuffix?: string;
  analystTitleSuffix?: string;
  showSourceLabel?: boolean;
  sourceLabel?: string;

  showBookmarkButton?: boolean;
  isBookmarked?: boolean;
  isBookmarkBusy?: boolean;
  onBookmarkClick?: () => void;
}

const ResearchGrowthTables: React.FC<ResearchGrowthTablesProps> = ({
  isDarkMode,
  selectedStock,
  selectedPeriod,
  mode = 'page',
  asOfDate,
  rangeStart,
  selectedPeriodType = 'Annual Data',
  analysisDateMode = 'as-reported',
  financialsSource = 'fmp',
  sections = 'all',
  useFrame = true,
  calcTitleSuffix = '',
  analystTitleSuffix = '',

  showBookmarkButton = false,
  isBookmarked = false,
  isBookmarkBusy = false,
  onBookmarkClick,
  showSourceLabel = false,
  sourceLabel,
}) => {
  const [keyMetricsAnnual, setKeyMetricsAnnual] = useState<KeyMetrics[]>([]);
  const [keyMetricsQuarter, setKeyMetricsQuarter] = useState<KeyMetrics[]>([]);
  const [incomeAnnual, setIncomeAnnual] = useState<IncomeStatement[]>([]);
  const [cashAnnual, setCashAnnual] = useState<CashFlowStatement[]>([]);
  const [incomeQuarter, setIncomeQuarter] = useState<IncomeStatement[]>([]);
  const [cashQuarter, setCashQuarter] = useState<CashFlowStatement[]>([]);
  const [incomeTTM, setIncomeTTM] = useState<IncomeStatement | null>(null);
  const [cashTTM, setCashTTM] = useState<CashFlowStatement | null>(null);
  const [analystEstimates, setAnalystEstimates] = useState<AnalystEstimate[]>([]);
  const [loading, setLoading] = useState(true);

  const rangeEndTs = useMemo(() => {
    if (!asOfDate) return Number.POSITIVE_INFINITY;
    if (asOfDate instanceof Date) {
      const t = asOfDate.getTime();
      return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
    }
    const key = String(asOfDate).trim();
    if (!key) return Number.POSITIVE_INFINITY;
    const t = Date.parse(`${key}T23:59:59.999Z`);
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  }, [asOfDate]);

  const rangeStartTs = useMemo(() => {
    if (!rangeStart) return Number.NEGATIVE_INFINITY;
    if (rangeStart instanceof Date) {
      const t = rangeStart.getTime();
      return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
    }
    const key = String(rangeStart).trim();
    if (!key) return Number.NEGATIVE_INFINITY;
    const t = Date.parse(`${key}T00:00:00.000Z`);
    return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
  }, [rangeStart]);

  const filterByVisibleRange = useMemo(() => {
    return <T extends { date: string; filingDate?: string; acceptedDate?: string; reportDate?: string; earningDate?: string }>(rows: T[]): T[] => {
      const hasStart = Number.isFinite(rangeStartTs) && rangeStartTs !== Number.NEGATIVE_INFINITY;
      const hasEnd = Number.isFinite(rangeEndTs) && rangeEndTs !== Number.POSITIVE_INFINITY;
      if (!hasStart && !hasEnd) return rows;

      const getAnchorDate = (row: T): string => {
        if (analysisDateMode === 'at-reported') {
          return String(row?.filingDate || row?.acceptedDate || row?.reportDate || row?.earningDate || row?.date || '');
        }
        return String(row?.date || '');
      };

      return rows.filter((r) => {
        const t = Date.parse(getAnchorDate(r));
        if (!Number.isFinite(t)) return false;
        if (hasStart && t < rangeStartTs) return false;
        if (hasEnd && t > rangeEndTs) return false;
        return true;
      });
    };
  }, [analysisDateMode, rangeStartTs, rangeEndTs]);

  const incomeAnnualEffective = useMemo(() => filterByVisibleRange(incomeAnnual), [filterByVisibleRange, incomeAnnual]);
  const cashAnnualEffective = useMemo(() => filterByVisibleRange(cashAnnual), [filterByVisibleRange, cashAnnual]);
  const incomeQuarterEffective = useMemo(() => filterByVisibleRange(incomeQuarter), [filterByVisibleRange, incomeQuarter]);
  const cashQuarterEffective = useMemo(() => filterByVisibleRange(cashQuarter), [filterByVisibleRange, cashQuarter]);
  const keyMetricsAnnualEffective = useMemo(() => filterByVisibleRange(keyMetricsAnnual), [filterByVisibleRange, keyMetricsAnnual]);
  const keyMetricsQuarterEffective = useMemo(() => filterByVisibleRange(keyMetricsQuarter), [filterByVisibleRange, keyMetricsQuarter]);

  useEffect(() => {
    setLoading(true);

    // Back Testing uses historical "as-of" dates; we need enough quarterly history so TTM growth
    // (which requires 8 quarters: current 4 + prior 4) doesn't disappear when moving the end date back.
    const quarterHistoryLimit = mode === 'embedded' ? 80 : 15;

    // For the embedded Back Testing view we also need enough ANNUAL history to consistently render
    // a full 10-year window anchored to the right slider.
    const annualHistoryLimit = mode === 'embedded' ? 40 : 15;

    const useFiscal = financialsSource === 'fiscal';

    const keyMetricsAnnualPromise = useFiscal
      ? fetchFiscalRatios(selectedStock, 'annual', annualHistoryLimit)
      : fetchKeyMetrics(selectedStock, 'annual', annualHistoryLimit);
    const keyMetricsQuarterPromise = useFiscal
      ? fetchFiscalRatios(selectedStock, 'quarter', quarterHistoryLimit)
      : fetchKeyMetrics(selectedStock, 'quarter', quarterHistoryLimit);

    const incomeAnnualPromise = useFiscal
      ? fetchFiscalIncomeStatement(selectedStock, 'annual', annualHistoryLimit)
      : fetchIncomeStatement(selectedStock, 'annual', annualHistoryLimit);
    const cashAnnualPromise = useFiscal
      ? fetchFiscalCashFlowStatement(selectedStock, 'annual', annualHistoryLimit)
      : fetchCashFlowStatement(selectedStock, 'annual', annualHistoryLimit);
    const incomeQuarterPromise = useFiscal
      ? fetchFiscalIncomeStatement(selectedStock, 'quarter', quarterHistoryLimit)
      : fetchIncomeStatement(selectedStock, 'quarter', quarterHistoryLimit);
    const cashQuarterPromise = useFiscal
      ? fetchFiscalCashFlowStatement(selectedStock, 'quarter', quarterHistoryLimit)
      : fetchCashFlowStatement(selectedStock, 'quarter', quarterHistoryLimit);

    const incomeTTMPromise = useFiscal ? Promise.resolve(null) : fetchIncomeStatementTTM(selectedStock, 1);
    const cashTTMPromise = useFiscal ? Promise.resolve(null) : fetchCashFlowStatementTTM(selectedStock, 1);

    Promise.allSettled([
      // Profitability (ROIC) needs both annual + quarterly key-metrics
      keyMetricsAnnualPromise,
      keyMetricsQuarterPromise,

      // Financials (Annuals)
      incomeAnnualPromise,
      cashAnnualPromise,

      // Financials (Quarterly)
      incomeQuarterPromise,
      cashQuarterPromise,

      // Financials (TTM) - AS-IS from API (FMP only)
      incomeTTMPromise,
      cashTTMPromise,

      // Analyst Estimates should always come from FMP and remain available
      // even if some other data requests fail.
      fetchAnalystEstimates(selectedStock, 'annual', 80),
    ])
      .then((results) => {
        const [kmAnnualRes, kmQuarterRes, incomeARes, cashARes, incomeQRes, cashQRes, incomeTtmRes, cashTtmRes, analystRes] = results;

        setKeyMetricsAnnual(kmAnnualRes.status === 'fulfilled' ? kmAnnualRes.value : []);
        setKeyMetricsQuarter(kmQuarterRes.status === 'fulfilled' ? kmQuarterRes.value : []);
        setIncomeAnnual(incomeARes.status === 'fulfilled' ? incomeARes.value : []);
        setCashAnnual(cashARes.status === 'fulfilled' ? cashARes.value : []);
        setIncomeQuarter(incomeQRes.status === 'fulfilled' ? incomeQRes.value : []);
        setCashQuarter(cashQRes.status === 'fulfilled' ? cashQRes.value : []);
        setIncomeTTM(incomeTtmRes.status === 'fulfilled' ? incomeTtmRes.value : null);
        setCashTTM(cashTtmRes.status === 'fulfilled' ? cashTtmRes.value : null);
        setAnalystEstimates(analystRes.status === 'fulfilled' ? analystRes.value : []);
        setLoading(false);
      })
      .catch(() => {
        // Keep previous behavior: just stop loading; tables will show '-' where data is missing.
        setLoading(false);
      });
  }, [financialsSource, selectedStock]);

  // --- Styling ---
  const basePageStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    color: isDarkMode ? '#e5e7eb' : '#1f2937',
    fontFamily: 'Manrope, Arial, sans-serif',
    boxSizing: 'border-box',
  };

  const containerStyle: React.CSSProperties =
    mode === 'page'
      ? {
          ...basePageStyle,
          padding: '2rem',
          minHeight: '100vh',
        }
      : {
          ...basePageStyle,
          padding: '0.75rem 2.5rem 1.5rem 2.5rem',
          minHeight: 'unset',
        };

  const frameStyle: React.CSSProperties =
    mode === 'page'
      ? {
          ...containerStyle,
          border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
          padding: '0.3rem 0.5rem',
          borderRadius: '8px',
          width: '92%',
          marginLeft: '3rem',
        }
      : {
          ...containerStyle,
          borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
        };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '0.88rem',
    fontWeight: 300,
    margin: '1.5rem 0 0.5rem 0',
    color: isDarkMode ? '#fff' : '#0D0D0D',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    marginBottom: '2rem',
  };

  const combinedTableStyle: React.CSSProperties = {
    ...tableStyle,
    tableLayout: 'fixed',
  };

  const thStyle: React.CSSProperties = {
    background: isDarkMode ? '#1F2937' : '#f5f5f5',
    color: isDarkMode ? '#fff' : '#0D0D0D',
    fontWeight: 500,
    padding: '8px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    textAlign: 'center',
    fontSize: '14px',
  };

  const tdStyle: React.CSSProperties = {
    padding: '8px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    textAlign: 'center',
    background: isDarkMode ? '#111827' : '#ffffff',
    color: isDarkMode ? '#e5e7eb' : '#0D0D0D',
  };

  const leftTdStyle: React.CSSProperties = { ...tdStyle, textAlign: 'left', fontWeight: 600 };
  const dividerBorder =
    mode === 'embedded'
      ? (isDarkMode ? '3px solid #555' : '3px solid #999')
      : (isDarkMode ? '2px solid #1F2937' : '2px solid #e0e0e0');
  const firstColWidthPx = 180;
  const firstColHeaderStyle: React.CSSProperties = { ...thStyle, width: firstColWidthPx, minWidth: firstColWidthPx, textAlign: 'left' };
  const firstColCellStyle: React.CSSProperties = { ...leftTdStyle, width: firstColWidthPx, minWidth: firstColWidthPx };

  const cmgrMetricColWidth = `calc((100% - ${firstColWidthPx}px) / 4)`;
  const cmgrMetricHeaderStyle: React.CSSProperties = {
    ...thStyle,
    width: cmgrMetricColWidth,
    minWidth: cmgrMetricColWidth,
    maxWidth: cmgrMetricColWidth,
  };
  const cmgrMetricCellStyle: React.CSSProperties = {
    ...tdStyle,
    width: cmgrMetricColWidth,
    minWidth: cmgrMetricColWidth,
    maxWidth: cmgrMetricColWidth,
  };

  // --- Helper functions ---
  const formatPeriodLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    const month = d.toLocaleString('en-US', { month: 'short' });
    const yy = String(d.getFullYear()).slice(-2);
    return `${month} ${yy}`;
  };

  const formatAtReportedLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const yy = String(d.getFullYear()).slice(-2);
    return `${day} ${month} ${yy}`;
  };

  const useAtReportedAnchors = analysisDateMode === 'at-reported' && mode === 'embedded';

  const getAnchorDateKey = <T extends { date: string; filingDate?: string; acceptedDate?: string; reportDate?: string; earningDate?: string }>(
    row: T | null | undefined
  ): string => {
    if (!row) return '';
    if (!useAtReportedAnchors) return String(row.date || '');
    const anchor = row.filingDate || row.acceptedDate || row.reportDate || row.earningDate || row.date;
    return String(anchor || row.date || '');
  };

  const formatDisplayPeriodLabel = <T extends { date: string; filingDate?: string; acceptedDate?: string; reportDate?: string; earningDate?: string }>(
    row: T
  ) => {
    const key = getAnchorDateKey(row);
    return useAtReportedAnchors ? formatAtReportedLabel(key) : formatPeriodLabel(key);
  };

  // Match Income Statement / Cash Flow formatting: 2 decimals, B/M suffix, no K.
  const formatStatementNumber = (value: number | undefined): string => {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '-';
    const abs = Math.abs(value);
    if (abs >= 1e9) return (value / 1e9).toFixed(2) + 'B';
    if (abs >= 1e6) return (value / 1e6).toFixed(2) + 'M';
    return value.toFixed(2);
  };

  const formatFinancialMetric = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow',
    value: number | undefined
  ) => {
    // Keep Financials table identical to the other statement tabs.
    return formatStatementNumber(value);
  };

  const sortByDateDesc = <T extends { date: string; filingDate?: string; acceptedDate?: string; reportDate?: string; earningDate?: string }>(arr: T[]) =>
    [...arr]
      .filter((d) => d.date)
      .sort((a, b) => {
        const ta = Date.parse(getAnchorDateKey(a));
        const tb = Date.parse(getAnchorDateKey(b));

        const aValid = Number.isFinite(ta);
        const bValid = Number.isFinite(tb);

        if (aValid && bValid) return tb - ta;
        if (aValid && !bValid) return -1;
        if (!aValid && bValid) return 1;
        return String(getAnchorDateKey(b)).localeCompare(String(getAnchorDateKey(a)));
      });

  const getCashByDate = (date: string, cashArr: CashFlowStatement[]) => cashArr.find((cf) => cf.date === date);

  const getKeyMetricByDate = (targetDate: string, period: 'annual' | 'quarter', kmArr: KeyMetrics[]) => {
    if (!targetDate) return null;

    // Fast path: exact match.
    const exact = kmArr.find((k) => k?.date === targetDate);
    if (exact) return exact;

    const targetTs = Date.parse(targetDate);
    if (!Number.isFinite(targetTs)) return null;

    // Quarterly: match by the same visible period label (month + year) first.
    // This keeps the table's ROIC aligned with the Key Metrics ROIC chart, which also labels by month+year.
    // Using the rendered label avoids timezone-related "month drift" for boundary dates.
    if (period === 'quarter') {
      const targetLabel = formatPeriodLabel(targetDate);

      const sameLabel = kmArr.filter((k) => {
        if (!k?.date) return false;
        return formatPeriodLabel(k.date) === targetLabel;
      });

      if (sameLabel.length) {
        let best: KeyMetrics | null = null;
        let bestDiff = Number.POSITIVE_INFINITY;
        for (const k of sameLabel) {
          const ts = k?.date ? Date.parse(k.date) : NaN;
          if (!Number.isFinite(ts)) continue;
          const diff = Math.abs(ts - targetTs);
          if (diff < bestDiff) {
            bestDiff = diff;
            best = k;
          }
        }
        return best;
      }
    }

    // Robust matching: pick the closest key-metric row by date.
    // This avoids wrong quarter selection when the API dates differ by a few days.
    let best: KeyMetrics | null = null;
    let bestDiff = Number.POSITIVE_INFINITY;
    for (const k of kmArr) {
      const ts = k?.date ? Date.parse(k.date) : NaN;
      if (!Number.isFinite(ts)) continue;
      const diff = Math.abs(ts - targetTs);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = k;
      }
    }

    const maxDays = period === 'quarter' ? 70 : 200;
    const maxDiffMs = maxDays * 24 * 60 * 60 * 1000;
    if (best && bestDiff <= maxDiffMs) return best;

    // Annual fallback: if we still didn't match, try same fiscal year.
    if (period === 'annual') {
      const year = new Date(targetDate).getFullYear();
      return kmArr.find((k) => k?.date && new Date(k.date).getFullYear() === year) ?? null;
    }

    return null;
  };

  const formatPercent = (value: number | undefined, fractionDigits: number = 1) => {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '-';
    return `${(value * 100).toFixed(fractionDigits)}%`;
  };

  const formatPercentFromAlreadyPercent = (value: number | undefined, fractionDigits: number = 1) => {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '-';
    return `${value.toFixed(fractionDigits)}%`;
  };

  // Get annual periods and years from incomeAnnual (sorted oldest to newest)
  const annualPeriods = useMemo(
    () => incomeAnnualEffective.map((d) => d.date).filter(Boolean).sort(),
    [incomeAnnualEffective]
  );

  const allAnnualPeriods = useMemo(() => incomeAnnual.map((d) => d.date).filter(Boolean).sort(), [incomeAnnual]);

  const getYear = useCallback((dateStr: string) => {
    const d = new Date(dateStr);
    return d.getFullYear();
  }, []);

  const lastYear = annualPeriods.length > 0 ? getYear(annualPeriods[annualPeriods.length - 1]) : new Date().getFullYear();

  const latestAnnualYearAll = useMemo(() => {
    if (allAnnualPeriods.length === 0) return lastYear;
    const y = getYear(allAnnualPeriods[allAnnualPeriods.length - 1]);
    return Number.isFinite(y) ? y : lastYear;
  }, [allAnnualPeriods, lastYear]);

  const isEmbeddedBackTesting = mode === 'embedded' && Boolean(asOfDate);

  // Back Testing requirement:
  // - The table should change when scrubbing time (header + values).
  // - Show a fixed 5-year forward horizon from the current as-of point.
  const estimateBaseYear = useMemo(() => {
    if (!isEmbeddedBackTesting) return lastYear;

    // In Back Testing, anchor to the latest ANNUAL period visible in the current range.
    // This avoids a +1 year drift when the as-of date is in Q1 of the next calendar year
    // while the latest full annual data is still the prior year.
    if (annualPeriods.length > 0 && Number.isFinite(lastYear)) {
      return Math.min(lastYear, latestAnnualYearAll);
    }

    const asOfTs = rangeEndTs;
    if (!Number.isFinite(asOfTs) || asOfTs === Number.POSITIVE_INFINITY) return latestAnnualYearAll;
    const asOfYear = new Date(asOfTs).getUTCFullYear();
    if (!Number.isFinite(asOfYear)) return latestAnnualYearAll;

    // Keep Back Testing aligned with Growth on initial load: if the right-edge
    // date is beyond the latest reported annual actual, anchor the estimate base
    // to that latest actual year (not to a future calendar year).
    return Math.min(asOfYear, latestAnnualYearAll);
  }, [annualPeriods.length, isEmbeddedBackTesting, lastYear, latestAnnualYearAll, rangeEndTs]);

  const cagrLabelBaseYear = isEmbeddedBackTesting ? estimateBaseYear : lastYear;

  // These labels are tied to the CAGR calculation windows:
  // - 5y CAGR uses start=baseYear-5 and end=baseYear (5 steps)
  // - 3y CAGR uses start=baseYear-3 and end=baseYear (3 steps)
  // - 1y CAGR uses start=baseYear-1 and end=baseYear (1 step)
  const fiveYearsAgo = cagrLabelBaseYear - 5;
  const threeYearsAgo = cagrLabelBaseYear - 3;
  const oneYearAgo = cagrLabelBaseYear - 1;

  // UX requirement: cap Analyst Estimates to 6 years (6 columns).
  const maxEstimateYearsToShow = 6;

  const analystEstimateYearsSet = useMemo(() => {
    const years = (analystEstimates ?? [])
      .map((e) => {
        if (!e?.date) return NaN;
        const y = new Date(e.date).getFullYear();
        return Number.isFinite(y) ? y : NaN;
      })
      .filter((y) => Number.isFinite(y)) as number[];
    return new Set<number>(years);
  }, [analystEstimates]);

  const estimateYears = useMemo(() => {
    const maxYearsToShow = maxEstimateYearsToShow;
    const years = Array.from(analystEstimateYearsSet).filter((y) => y > estimateBaseYear);

    const uniqueSorted = Array.from(new Set(years)).sort((a, b) => a - b);
    const picked = uniqueSorted.slice(0, maxYearsToShow);

    // Fallback (no estimates or API disabled): keep a stable 6-column layout.
    if (picked.length === 0)
      return [
        estimateBaseYear + 1,
        estimateBaseYear + 2,
        estimateBaseYear + 3,
        estimateBaseYear + 4,
        estimateBaseYear + 5,
        estimateBaseYear + 6,
      ];
    return picked;
  }, [analystEstimateYearsSet, estimateBaseYear, maxEstimateYearsToShow]);

  const estimateYearsToRender = useMemo(() => {
    if (!isEmbeddedBackTesting) return estimateYears;

    const asOfYears = Array.from({ length: 5 }, (_, i) => estimateBaseYear + i + 1);
    const hasAsOfEstimateData = asOfYears.some((y) => analystEstimateYearsSet.has(y));
    if (hasAsOfEstimateData) return asOfYears;

    // Initial Back Testing load can point to a range that has no forward estimate years
    // for this symbol; fall back to the same forward window used by Growth.
    return Array.from({ length: 5 }, (_, i) => lastYear + i + 1);
  }, [analystEstimateYearsSet, estimateBaseYear, estimateYears, isEmbeddedBackTesting, lastYear]);

  const estimateCagrBaseYear = useMemo(() => {
    const firstRenderedYear = estimateYearsToRender[0];
    if (!Number.isFinite(firstRenderedYear)) return estimateBaseYear;
    return firstRenderedYear - 1;
  }, [estimateBaseYear, estimateYearsToRender]);

  const analystFlexGrow = Math.max(1, estimateYearsToRender.length / 3);

  const findEstimateForYear = (year: number) => {
    const matches = (analystEstimates ?? []).filter((e) => {
      if (!e?.date) return false;
      const y = new Date(e.date).getFullYear();
      return y === year;
    });

    if (matches.length === 0) return undefined;
    if (matches.length === 1) return matches[0];

    // If multiple rows exist for the same year, prefer the latest date.
    return matches
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  };

  const formatPercentChange = (current: number | undefined, previous: number | undefined) => {
    if (typeof current !== 'number' || Number.isNaN(current)) return '-';
    if (typeof previous !== 'number' || Number.isNaN(previous) || previous === 0) return '-';
    const pct = ((current / previous) - 1) * 100;
    if (Number.isNaN(pct) || !Number.isFinite(pct)) return '-';
    return `${pct.toFixed(1)}%`;
  };

  const normalizeGrowthDisplayValue = (value: string): string => {
    const trimmed = (value ?? '').trim();
    if (!trimmed || trimmed === '-') return 'N/A';
    if (/nan/i.test(trimmed) || /infinity/i.test(trimmed)) return 'N/A';
    return trimmed;
  };

  // Growth Calculation (CAGR) table:
  // - Show computed growth even if it's negative (styled red).
  // - Still return N/A when inputs are missing/non-finite.
  const normalizeCagrTableValue = (value: string): string => {
    const normalized = normalizeGrowthDisplayValue(value);
    if (normalized === 'N/A') return 'N/A';
    const match = normalized.match(/-?\d+(?:\.\d+)?/);
    if (!match) return normalized;
    const numericPercent = Number(match[0]);
    if (!Number.isFinite(numericPercent)) return 'N/A';
    return normalized;
  };

  const getPercentCellStyle = (value: string): React.CSSProperties => {
    const v = (value ?? '').trim();
    if (v === 'N/A') return { color: 'red' };

    const match = v.match(/-?\d+(?:\.\d+)?/);
    if (!match) return {};
    const numericPercent = Number(match[0]);
    if (!Number.isFinite(numericPercent)) return {};
    if (numericPercent < 0) return { color: 'red' };
    return numericPercent < 10 ? { color: 'red' } : {};
  };

  const getNegativeNumberCellStyle = (value: number | undefined): React.CSSProperties => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return {};
    return value < 0 ? { color: 'red' } : {};
  };

  const getNegativeStringCellStyle = (value: string): React.CSSProperties => {
    const v = (value ?? '').trim();
    if (!v || v === '-' || v === 'N/A') return {};
    return v.startsWith('-') ? { color: 'red' } : {};
  };

  const warningValueWrapStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const renderWithWarningIcon = (value: React.ReactNode, style: React.CSSProperties): React.ReactNode => {
    const color = String(style?.color ?? '').trim().toLowerCase();
    const isRed = color === 'red' || color === '#ff0000' || color === '#f00';
    if (!isRed) return value;

    const text = typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '';
    if (!text || text === '-' || text === 'N/A') return value;

    // Only show the warning icon when the rendered value is actually negative.
    // (Some positive values are styled red due to thresholds, e.g. < 10%.)
    const isNegative = text.startsWith('-');
    if (!isNegative) return value;

    return (
      <span style={warningValueWrapStyle}>
        <img src="/images/warning.svg" alt="" aria-hidden="true" style={{ width: 14, height: 14, display: 'block', flex: '0 0 auto' }} />
        <span>{value}</span>
      </span>
    );
  };

  const getActualIncomeStatementForYear = (year: number) =>
    incomeAnnual.find((d) => d.date && new Date(d.date).getFullYear() === year);

  const getEstimateMetricValue = (estimate: AnalystEstimate | undefined, metric: 'revenue' | 'netIncome' | 'eps') => {
    if (!estimate) return undefined;
    if (metric === 'revenue') return estimate.estimatedRevenue;
    if (metric === 'netIncome') return estimate.estimatedNetIncome;
    return estimate.estimatedEps;
  };

  const getActualMetricValue = (year: number, metric: 'revenue' | 'netIncome' | 'eps') => {
    const actual = getActualIncomeStatementForYear(year);
    if (!actual) return undefined;
    if (metric === 'revenue') return actual.revenue;
    if (metric === 'netIncome') return actual.netIncome;
    return actual.eps;
  };

  const getEstimatePercentForYear = (year: number, metric: 'revenue' | 'netIncome' | 'eps') => {
    const currentEst = findEstimateForYear(year);
    const prevEst = findEstimateForYear(year - 1);
    const currentVal = getEstimateMetricValue(currentEst, metric);
    const prevActual = getActualMetricValue(year - 1, metric);
    const prevVal = prevActual ?? getEstimateMetricValue(prevEst, metric);
    return formatPercentChange(currentVal, prevVal);
  };

  const estimateCagrWindow = useMemo(() => {
    if (estimateYearsToRender.length >= 1) {
      const start = estimateCagrBaseYear;
      const end = estimateYearsToRender[estimateYearsToRender.length - 1];
      const years = end - start;
      return { startYear: start, endYear: end, years };
    }
    return { startYear: estimateCagrBaseYear, endYear: estimateCagrBaseYear, years: 0 };
  }, [estimateCagrBaseYear, estimateYearsToRender]);

  const getEstimateCagrForMetric = (metric: 'revenue' | 'netIncome' | 'eps') => {
    const { startYear, endYear, years } = estimateCagrWindow;
    if (years <= 0) return 'N/A';
    const startVal = getActualMetricValue(startYear, metric);
    const endVal = getEstimateMetricValue(findEstimateForYear(endYear), metric);
    return normalizeCagrTableValue(calcCMGR(startVal ?? NaN, endVal ?? NaN, years));
  };

  // CAGR calculation
  function calcCMGR(start: number, end: number, years: number) {
    if (!Number.isFinite(start) || !Number.isFinite(end) || years <= 0) return 'N/A';
    // For CAGR to be meaningful in this table, both endpoints must be > 0.
    // (If any raw input is negative/zero, we intentionally show N/A.)
    if (start <= 0 || end <= 0) return 'N/A';

    const ratio = end / start;
    const cagr = (Math.pow(ratio, 1 / years) - 1) * 100;
    if (!Number.isFinite(cagr)) return 'N/A';
    return `${cagr.toFixed(1)}%`;
  }

  // Field accessors for IncomeStatement
  const fieldAccessors: { [key: string]: (d: IncomeStatement) => number | undefined } = {
    revenue: (d) => d.revenue,
    grossProfit: (d) => d.grossProfit,
    operatingIncome: (d) => d.operatingIncome,
    netIncome: (d) => d.netIncome,
    eps: (d) => (d as any).epsDiluted ?? d.eps,
    operatingCashFlow: (d) =>
      (cashAnnualEffective.find((cf) => cf.date === d.date)?.netCashProvidedByOperatingActivities ??
        cashAnnualEffective.find((cf) => cf.date === d.date)?.operatingCashFlow) ??
      undefined,
  };

  // For each field, get values for 5y, 3y, 1y
  const getCMGR = (field: string, years: number) => {
    const accessor = fieldAccessors[field];
    if (!accessor) return '';
    const sorted = [...incomeAnnualEffective].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const values = sorted.map(accessor).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (values.length < years + 1) return '';
    const start = values[values.length - (years + 1)];
    const end = values[values.length - 1];
    return calcCMGR(start, end, years);
  };

  // Financials table sources
  // We want Annual columns displayed oldest → newest (e.g., Sep 16 … Sep 25)
  // while still limiting to the latest 10 annual periods.
  const annualLimit = mode === 'embedded' ? 10 : selectedPeriodType === 'Annual Data' ? 10 : 6;
  const annualIncomeCols = sortByDateDesc(incomeAnnualEffective).slice(0, annualLimit).reverse();
  const annualCashCols = sortByDateDesc(cashAnnualEffective);

  // Match other tabs: show quarters oldest → newest (latest quarter on the far right)
  // We want the last 5 quarters, consistent with the statement tabs (Balance Sheet / Income Statement / Cash Flow).
  // Keep a larger window only for TTM mode (useful context when comparing to rolling-4 calculations).
  const quarterlyLimit = mode === 'embedded' ? 5 : selectedPeriodType === 'TTM' ? 8 : 5;
  const quarterlyIncomeCols = sortByDateDesc(incomeQuarterEffective).slice(0, quarterlyLimit).reverse();
  const quarterlyCashCols = sortByDateDesc(cashQuarterEffective);

  const quarterlyIncomeForTTM = sortByDateDesc(incomeQuarterEffective).slice(0, 4);
  const quarterlyCashForTTM = sortByDateDesc(cashQuarterEffective).slice(0, 4);

  // Profitability sources
  const annualIncomeForProfit = annualIncomeCols;
  const quarterlyIncomeForProfitCols = quarterlyIncomeCols;

  const latestAnnualYear = useMemo(() => {
    if (annualIncomeCols.length === 0) return null;
    const last = annualIncomeCols[annualIncomeCols.length - 1]?.date;
    if (!last) return null;
    const y = new Date(last).getFullYear();
    return Number.isFinite(y) ? y : null;
  }, [annualIncomeCols]);

  const latestAnnualDateKey = useMemo(() => {
    if (annualIncomeCols.length === 0) return null;
    return annualIncomeCols[annualIncomeCols.length - 1]?.date ?? null;
  }, [annualIncomeCols]);

  const latestQuarterDateForAnnualYear = useMemo(() => {
    if (latestAnnualYear == null) return null;
    const matches = quarterlyIncomeCols.filter((q) => {
      const y = new Date(q.date).getFullYear();
      return Number.isFinite(y) && y === latestAnnualYear;
    });
    if (matches.length === 0) return null;
    return matches[matches.length - 1]?.date ?? null;
  }, [latestAnnualYear, quarterlyIncomeCols]);

  const getHighlightedQuarterDate = useCallback(
    (quarters: Array<{ date: string }>) => {
      if (latestAnnualDateKey) {
        const match = quarters.find((q) => q.date === latestAnnualDateKey);
        if (match) return latestAnnualDateKey;
      }
      return latestQuarterDateForAnnualYear;
    },
    [latestAnnualDateKey, latestQuarterDateForAnnualYear]
  );

  const highlightedQuarterDate = useMemo(
    () => getHighlightedQuarterDate(quarterlyIncomeCols),
    [getHighlightedQuarterDate, quarterlyIncomeCols]
  );

  const highlightedQuarterDateForProfit = useMemo(
    () => getHighlightedQuarterDate(quarterlyIncomeForProfitCols),
    [getHighlightedQuarterDate, quarterlyIncomeForProfitCols]
  );

  const sumLast4 = (arr: number[]) => arr.slice(0, 4).reduce((a, b) => a + b, 0);

  const useDirectTTM = !asOfDate && financialsSource === 'fmp';

  const getQuarterlyTTMMargin = (kind: 'gross' | 'operating' | 'net') => {
    // Default behavior: prefer AS-IS TTM endpoint.
    // Back Testing (asOfDate set): compute historical-as-of from last 4 quarters.
    if (useDirectTTM && incomeTTM && typeof incomeTTM.revenue === 'number' && incomeTTM.revenue !== 0) {
      const rev = incomeTTM.revenue;
      const num = kind === 'gross' ? incomeTTM.grossProfit : kind === 'operating' ? incomeTTM.operatingIncome : incomeTTM.netIncome;
      if (typeof num === 'number') {
        return formatPercentFromAlreadyPercent((num / rev) * 100, 1);
      }
    }

    const last4 = sortByDateDesc(incomeQuarterEffective).slice(0, 4);
    if (last4.length < 4) return '-';
    const rev = last4.map((q) => q.revenue).filter((v) => typeof v === 'number');
    if (rev.length < 4) return '-';
    const num = last4
      .map((q) => {
        if (kind === 'gross') return q.grossProfit;
        if (kind === 'operating') return q.operatingIncome;
        return q.netIncome;
      })
      .filter((v) => typeof v === 'number');
    if (num.length < 4) return '-';
    const denom = sumLast4(rev);
    if (denom === 0) return '-';
    return formatPercentFromAlreadyPercent((sumLast4(num) / denom) * 100, 1);
  };

  const getROICDisplay = (date: string, period: 'annual' | 'quarter') => {
    const km = getKeyMetricByDate(date, period, period === 'annual' ? keyMetricsAnnualEffective : keyMetricsQuarterEffective);
    const roicValue = (km as any)?.returnOnInvestedCapital ?? (km as any)?.roic;
    // The API usually returns ROIC already as a ratio (0.12). If it comes as percent (12), still show sensibly.
    if (typeof roicValue !== 'number' || Number.isNaN(roicValue) || !Number.isFinite(roicValue)) return '-';
    return roicValue > 1 ? formatPercentFromAlreadyPercent(roicValue, 1) : formatPercent(roicValue, 1);
  };

  // Match Key Metrics ROIC chart (Quarterly view): annualize quarterly ROIC when it's a fraction.
  // The chart annualizes only when the API returns a fractional ratio (e.g., 0.06 -> 24%).
  const getQuarterlyRoicAnnualizedDisplay = (date: string) => {
    const km = getKeyMetricByDate(date, 'quarter', keyMetricsQuarterEffective);
    const raw = (km as any)?.returnOnInvestedCapital ?? (km as any)?.roic;
    const roicQ = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(roicQ)) return '-';

    // If it's fractional, annualize x4 then convert to %.
    if (Math.abs(roicQ) < 1) {
      return formatPercentFromAlreadyPercent(roicQ * 4 * 100, 1);
    }

    // If it's already a percent (rare for this endpoint), show as-is.
    return formatPercentFromAlreadyPercent(roicQ, 1);
  };

  // ROIC (TTM) column: rolling 4 quarters using invested capital weighting (same as Key Metrics TTM chart).
  const roicTtmSeries = useMemo(() => {
    const kmAsc = sortByDateDesc(keyMetricsQuarterEffective).reverse();
    const byDate = new Map<string, number>();
    let latest: number | null = null;

    const getRoicFraction = (km: any): number | null => {
      const raw = (km as any)?.returnOnInvestedCapital ?? (km as any)?.roic;
      const num = typeof raw === 'number' ? raw : Number(raw);
      return Number.isFinite(num) ? num : null;
    };

    if (kmAsc.length < 4) return { byDate, latest };

    for (let i = 3; i < kmAsc.length; i++) {
      let nopatSum = 0;
      let investedCapSum = 0;
      let investedCapCount = 0;
      let roicSum = 0;
      let roicCount = 0;

      for (let j = i - 3; j <= i; j++) {
        const km = kmAsc[j];
        const icRaw = (km as any)?.investedCapital;
        const investedCapital = typeof icRaw === 'number' ? icRaw : Number(icRaw);
        const roicQ = getRoicFraction(km);
        if (roicQ !== null) {
          roicSum += roicQ;
          roicCount++;
        }
        if (!Number.isFinite(investedCapital) || investedCapital <= 0) continue;
        if (roicQ === null) continue;
        nopatSum += roicQ * investedCapital;
        investedCapSum += investedCapital;
        investedCapCount++;
      }

      const investedCapAvg = investedCapCount ? investedCapSum / investedCapCount : 0;
      const roicTtm = investedCapAvg > 0 ? nopatSum / investedCapAvg : roicCount ? roicSum / roicCount : NaN;
      if (!Number.isFinite(roicTtm)) continue;

      const keyDate = kmAsc[i]?.date;
      if (!keyDate) continue;
      byDate.set(keyDate, roicTtm);
      latest = roicTtm;
    }

    return { byDate, latest };
  }, [keyMetricsQuarterEffective]);

  const getQuarterlyTTM = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ) => {
    // Default behavior: prefer AS-IS TTM statement rows.
    // Back Testing (asOfDate set): compute historical-as-of from last 4 quarters.
    if (useDirectTTM) {
      if (field === 'operatingCashFlow') {
        const v = cashTTM?.netCashProvidedByOperatingActivities ?? cashTTM?.operatingCashFlow;
        if (typeof v === 'number') return formatFinancialMetric('operatingCashFlow', v);
      } else {
        const v =
          field === 'revenue'
            ? incomeTTM?.revenue
            : field === 'grossProfit'
              ? incomeTTM?.grossProfit
              : field === 'operatingIncome'
                ? incomeTTM?.operatingIncome
                : field === 'netIncome'
                  ? incomeTTM?.netIncome
                  : (incomeTTM as any)?.epsDiluted ?? incomeTTM?.eps;
        if (typeof v === 'number') return formatFinancialMetric(field, v);
      }
    }

    if (field === 'operatingCashFlow') {
      const vals = quarterlyCashForTTM
        .map((c) => c.netCashProvidedByOperatingActivities ?? c.operatingCashFlow)
        .filter((v) => typeof v === 'number');
      if (vals.length < 4) return '-';
      return formatFinancialMetric('operatingCashFlow', vals.reduce((a, b) => a + b, 0));
    }

    const vals = quarterlyIncomeForTTM
      .map((q) => {
        if (field === 'revenue') return q.revenue;
        if (field === 'grossProfit') return q.grossProfit;
        if (field === 'operatingIncome') return q.operatingIncome;
        if (field === 'netIncome') return q.netIncome;
        return (q as any)?.epsDiluted ?? q.eps;
      })
      .filter((v) => typeof v === 'number');
    if (vals.length < 4) return '-';
    return formatFinancialMetric(field, vals.reduce((a, b) => a + b, 0));
  };

  const sumQuarterlyIncomeField = (
    rows: IncomeStatement[],
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps'
  ) => {
    const nums = rows
      .map((q) => {
        if (field === 'revenue') return q.revenue;
        if (field === 'grossProfit') return q.grossProfit;
        if (field === 'operatingIncome') return q.operatingIncome;
        if (field === 'netIncome') return q.netIncome;
        return (q as any).epsDiluted ?? q.eps;
      })
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (nums.length < 4) return undefined;
    return nums.reduce((a, b) => a + b, 0);
  };

  const sumQuarterlyCashField = (rows: CashFlowStatement[], field: 'operatingCashFlow') => {
    const nums = rows
      .map((q) => (field === 'operatingCashFlow' ? (q.operatingCashFlow ?? (q as any).netCashProvidedByOperatingActivities) : undefined))
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (nums.length < 4) return undefined;
    return nums.reduce((a, b) => a + b, 0);
  };

  const getCashFreeCashFlow = (row: CashFlowStatement | null | undefined) => {
    if (!row) return undefined;

    if (typeof row.freeCashFlow === 'number' && Number.isFinite(row.freeCashFlow)) {
      return row.freeCashFlow;
    }

    const ocf = row.netCashProvidedByOperatingActivities ?? row.operatingCashFlow;
    if (typeof ocf !== 'number' || !Number.isFinite(ocf)) return undefined;

    const capex = row.capitalExpenditure ?? row.investmentsInPropertyPlantAndEquipment;
    if (typeof capex !== 'number' || !Number.isFinite(capex)) return undefined;

    return ocf + capex;
  };

  const sumQuarterlyFreeCashFlowField = (rows: CashFlowStatement[]) => {
    const nums = rows
      .map((q) => getCashFreeCashFlow(q))
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (nums.length < 4) return undefined;
    return nums.reduce((a, b) => a + b, 0);
  };

  const formatCashToNetIncomeRatioDisplay = (numerator: number | undefined, denominator: number | undefined) => {
    if (typeof numerator !== 'number' || !Number.isFinite(numerator)) return { value: '-', isNA: false };
    if (typeof denominator !== 'number' || !Number.isFinite(denominator) || denominator === 0) return { value: '-', isNA: false };
    if (numerator < 0 || denominator < 0) return { value: 'N/A', isNA: true };

    const ratio = (numerator / denominator) * 100;
    if (!Number.isFinite(ratio)) return { value: '-', isNA: false };
    return { value: formatPercentFromAlreadyPercent(ratio, 1), isNA: false };
  };

  const getQuarterlyTTMCashToNetIncomeRatio = (kind: 'ocf' | 'fcf') => {
    const ttmNetIncome = incomeTTM?.netIncome;
    const ttmNumerator =
      kind === 'ocf'
        ? cashTTM?.netCashProvidedByOperatingActivities ?? cashTTM?.operatingCashFlow
        : getCashFreeCashFlow(cashTTM ?? undefined);

    const ttmDisplay = formatCashToNetIncomeRatioDisplay(ttmNumerator, ttmNetIncome);
    if (ttmDisplay.value !== '-') return ttmDisplay;

    if (quarterlyIncomeForTTM.length < 4 || quarterlyCashForTTM.length < 4) return { value: '-', isNA: false };

    const netIncome = sumQuarterlyIncomeField(quarterlyIncomeForTTM, 'netIncome');
    const numerator = kind === 'ocf' ? sumQuarterlyCashField(quarterlyCashForTTM, 'operatingCashFlow') : sumQuarterlyFreeCashFlowField(quarterlyCashForTTM);
    return formatCashToNetIncomeRatioDisplay(numerator, netIncome);
  };

  // TTM growth % = (TTM(current 4Q) / TTM(prior 4Q) - 1) * 100
  const getTTMGrowthPercent = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ) => {
    const incomeDesc = sortByDateDesc(incomeQuarterEffective);
    const cashDesc = sortByDateDesc(cashQuarterEffective);

    const currentIncome4 = incomeDesc.slice(0, 4);
    const priorIncome4 = incomeDesc.slice(4, 8);
    const currentCash4 = cashDesc.slice(0, 4);
    const priorCash4 = cashDesc.slice(4, 8);

    const current =
      field === 'operatingCashFlow' ? sumQuarterlyCashField(currentCash4, 'operatingCashFlow') : sumQuarterlyIncomeField(currentIncome4, field);

    const prior =
      field === 'operatingCashFlow' ? sumQuarterlyCashField(priorCash4, 'operatingCashFlow') : sumQuarterlyIncomeField(priorIncome4, field);

    if (typeof current !== 'number' || typeof prior !== 'number' || prior === 0) return '-';

    const pct = ((current / prior) - 1) * 100;
    if (!Number.isFinite(pct)) return '-';
    return formatPercentFromAlreadyPercent(pct, 1);
  };

  const getLatestAnnualValue = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ): number | undefined => {
    const latestIncome = sortByDateDesc(incomeAnnualEffective)[0];
    if (!latestIncome) return undefined;

    if (field === 'operatingCashFlow') {
      const latestCash = cashAnnualEffective.find((cf) => cf.date === latestIncome.date);
      const ocf = latestCash?.netCashProvidedByOperatingActivities ?? latestCash?.operatingCashFlow;
      return typeof ocf === 'number' && Number.isFinite(ocf) ? ocf : undefined;
    }

    if (field === 'revenue') return latestIncome.revenue;
    if (field === 'grossProfit') return latestIncome.grossProfit;
    if (field === 'operatingIncome') return latestIncome.operatingIncome;
    if (field === 'netIncome') return latestIncome.netIncome;

    const eps = (latestIncome as any).epsDiluted ?? latestIncome.eps;
    return typeof eps === 'number' && Number.isFinite(eps) ? eps : undefined;
  };

  const getCurrentTtmValue = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ): number | undefined => {
    if (field === 'operatingCashFlow') {
      if (useDirectTTM) {
        const direct = cashTTM?.netCashProvidedByOperatingActivities ?? cashTTM?.operatingCashFlow;
        if (typeof direct === 'number' && Number.isFinite(direct)) return direct;
      }
      return sumQuarterlyCashField(sortByDateDesc(cashQuarterEffective).slice(0, 4), 'operatingCashFlow');
    }

    if (useDirectTTM && incomeTTM) {
      if (field === 'revenue' && typeof incomeTTM.revenue === 'number') return incomeTTM.revenue;
      if (field === 'grossProfit' && typeof incomeTTM.grossProfit === 'number') return incomeTTM.grossProfit;
      if (field === 'operatingIncome' && typeof incomeTTM.operatingIncome === 'number') return incomeTTM.operatingIncome;
      if (field === 'netIncome' && typeof incomeTTM.netIncome === 'number') return incomeTTM.netIncome;
      if (field === 'eps') {
        const eps = (incomeTTM as any).epsDiluted ?? incomeTTM.eps;
        if (typeof eps === 'number' && Number.isFinite(eps)) return eps;
      }
    }

    return sumQuarterlyIncomeField(sortByDateDesc(incomeQuarterEffective).slice(0, 4), field === 'eps' ? 'eps' : field);
  };

  const isLatestAnnualEqualToCurrentTtm = (
    field: 'revenue' | 'grossProfit' | 'operatingIncome' | 'netIncome' | 'eps' | 'operatingCashFlow'
  ): boolean => {
    const annual = getLatestAnnualValue(field);
    const ttm = getCurrentTtmValue(field);
    if (typeof annual !== 'number' || !Number.isFinite(annual)) return false;
    if (typeof ttm !== 'number' || !Number.isFinite(ttm)) return false;

    // If both values render identically in the table, treat them as equal.
    const annualDisplay = formatFinancialMetric(field, annual);
    const ttmDisplay = formatFinancialMetric(field, ttm);
    if (annualDisplay !== '-' && annualDisplay === ttmDisplay) return true;

    const tolerance = Math.max(1e-6, Math.abs(annual) * 0.001);
    return Math.abs(annual - ttm) <= tolerance;
  };

  const ttmLabelDate = useMemo(() => {
    const quarterlyLabel = quarterlyIncomeForTTM[0]?.date;
    if (quarterlyLabel) return quarterlyLabel;
    if (incomeTTM?.date) return incomeTTM.date;
    if (isEmbeddedBackTesting && asOfDate) return String(asOfDate);
    return '';
  }, [asOfDate, incomeTTM?.date, isEmbeddedBackTesting, quarterlyIncomeForTTM]);

  if (loading) {
    return (
      <div style={containerStyle}>
        <CenteredLoadingSpinner isDarkMode={isDarkMode} minHeight={mode === 'page' ? 'calc(100vh - 15rem)' : '240px'} />
      </div>
    );
  }

  const calcAnalystRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '2.5rem',
    marginBottom: '0rem',
    background: isDarkMode ? '#111827' : '#ffffff',
    position: 'relative',
    paddingRight: '2.25rem',
  };

  const sourceLabelStyle: React.CSSProperties = {
    marginLeft: '0.4rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
  };

  const calcContent = (
    <div id="growth-calc-cmgr-container" style={{ flexGrow: 2, flexShrink: 1, flexBasis: 0, minWidth: 0 }}>
      <div id="growth-calc-cmgr-title" style={{ ...sectionTitleStyle, margin: '0.5rem 0 0.5rem 0' }}>
        Growth Calculation (CAGR)
        {calcTitleSuffix ? <span style={sourceLabelStyle}>{calcTitleSuffix}</span> : null}
      </div>
      <table id="growth-calc-cmgr-table" style={{ ...tableStyle, tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={firstColHeaderStyle}>Fiscal Period</th>
            <th style={cmgrMetricHeaderStyle}>{`5 Years (${fiveYearsAgo}-${lastYear})`}</th>
            <th style={cmgrMetricHeaderStyle}>{`3 Years (${threeYearsAgo}-${lastYear})`}</th>
            <th style={cmgrMetricHeaderStyle}>{`1 Year (${oneYearAgo}-${lastYear})`}</th>
            <th
              style={cmgrMetricHeaderStyle}
            >{`TTM / ${formatPeriodLabel(getAnchorDateKey(quarterlyIncomeForTTM[0] as any) || ttmLabelDate)}`}</th>
          </tr>
        </thead>
        <tbody>
          {['revenue', 'grossProfit', 'operatingIncome', 'netIncome', 'eps', 'operatingCashFlow'].map((field) => (
            <tr key={field}>
              <td style={leftTdStyle}>
                {field === 'revenue' && 'Revenue'}
                {field === 'grossProfit' && 'Gross Profit'}
                {field === 'operatingIncome' && 'Operating Income'}
                {field === 'netIncome' && 'Net Income'}
                {field === 'eps' && 'EPS'}
                {field === 'operatingCashFlow' && 'Operating Cash Flow'}
              </td>
              {(() => {
                const v5 = normalizeCagrTableValue(getCMGR(field, 5));
                const v3 = normalizeCagrTableValue(getCMGR(field, 3));
                const v1Raw = getCMGR(field, 1);
                const vTtmRaw = getTTMGrowthPercent(field as any);
                const shouldAlignOneYearAndTtm = isLatestAnnualEqualToCurrentTtm(field as any);
                const v1 = normalizeCagrTableValue(v1Raw);
                const vTtm = normalizeCagrTableValue(shouldAlignOneYearAndTtm ? v1Raw : vTtmRaw);

                const cagrNAStyle: React.CSSProperties = { color: '#ff0000' };
                const styleFor = (v: string) => (v === 'N/A' ? cagrNAStyle : getPercentCellStyle(v));
                return (
                  <>
                    {(() => {
                      const s = { ...cmgrMetricCellStyle, ...styleFor(v5) };
                      return <td style={s}>{renderWithWarningIcon(v5, s)}</td>;
                    })()}
                    {(() => {
                      const s = { ...cmgrMetricCellStyle, ...styleFor(v3) };
                      return <td style={s}>{renderWithWarningIcon(v3, s)}</td>;
                    })()}
                    {(() => {
                      const s = { ...cmgrMetricCellStyle, ...styleFor(v1) };
                      return <td style={s}>{renderWithWarningIcon(v1, s)}</td>;
                    })()}
                    {(() => {
                      const s = { ...cmgrMetricCellStyle, ...styleFor(vTtm) };
                      return <td style={s}>{renderWithWarningIcon(vTtm, s)}</td>;
                    })()}
                  </>
                );
              })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const analystContent = (
    <div id="growth-calc-analyst-container" style={{ flexGrow: analystFlexGrow, flexShrink: 1, flexBasis: 0, minWidth: 0 }}>
      <div id="growth-calc-analyst-title" style={{ ...sectionTitleStyle, margin: '0.5rem 0 0.5rem 0', position: 'relative' }}>
        Analyst Estimates
        {analystTitleSuffix ? <span style={sourceLabelStyle}>{analystTitleSuffix}</span> : null}
        {showSourceLabel && sourceLabel ? (
          <span
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              fontSize: '0.7rem',
              fontWeight: 600,
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              pointerEvents: 'none',
            }}
          >
            {sourceLabel}
          </span>
        ) : null}
      </div>
      <table id="growth-calc-analyst-table" style={{ ...tableStyle, tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            <th style={firstColHeaderStyle}>Fiscal Period</th>
            {estimateYearsToRender.map((y, idx) => (
              <th style={thStyle} key={`est-h-${y}`}>{`Year ${idx + 1} (${y})`}</th>
            ))}
            <th style={{ ...thStyle, minWidth: 140, width: 140, fontWeight: 800 }}>
              <div style={{ lineHeight: 1.1 }}>{`${estimateCagrWindow.years} YEARS CAGR`}</div>
              <div style={{ lineHeight: 1.1 }}>{`(${estimateCagrWindow.startYear}-${estimateCagrWindow.endYear})`}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={leftTdStyle}>Revenue</td>
            {estimateYearsToRender.map((y) => {
              const v = getEstimatePercentForYear(y, 'revenue');
              const cellStyle = { ...tdStyle, ...getPercentCellStyle(v) };
              return (
                <td style={cellStyle} key={`est-r-${y}`}>
                  {renderWithWarningIcon(v, cellStyle)}
                </td>
              );
            })}
            {(() => {
              const v = getEstimateCagrForMetric('revenue');
              const cellStyle = { ...tdStyle, minWidth: 140, width: 140, ...getPercentCellStyle(v) };
              return <td style={cellStyle}>{renderWithWarningIcon(v, cellStyle)}</td>;
            })()}
          </tr>
          <tr>
            <td style={leftTdStyle}>Net Income</td>
            {estimateYearsToRender.map((y) => {
              const v = getEstimatePercentForYear(y, 'netIncome');
              const cellStyle = { ...tdStyle, ...getPercentCellStyle(v) };
              return (
                <td style={cellStyle} key={`est-ni-${y}`}>
                  {renderWithWarningIcon(v, cellStyle)}
                </td>
              );
            })}
            {(() => {
              const v = getEstimateCagrForMetric('netIncome');
              const cellStyle = { ...tdStyle, minWidth: 140, width: 140, ...getPercentCellStyle(v) };
              return <td style={cellStyle}>{renderWithWarningIcon(v, cellStyle)}</td>;
            })()}
          </tr>
          <tr>
            <td style={leftTdStyle}>EPS</td>
            {estimateYearsToRender.map((y) => {
              const v = getEstimatePercentForYear(y, 'eps');
              const cellStyle = { ...tdStyle, ...getPercentCellStyle(v) };
              return (
                <td style={cellStyle} key={`est-eps-${y}`}>
                  {renderWithWarningIcon(v, cellStyle)}
                </td>
              );
            })}
            {(() => {
              const v = getEstimateCagrForMetric('eps');
              const cellStyle = { ...tdStyle, minWidth: 140, width: 140, ...getPercentCellStyle(v) };
              return <td style={cellStyle}>{renderWithWarningIcon(v, cellStyle)}</td>;
            })()}
          </tr>
        </tbody>
      </table>
    </div>
  );

  const calcAnalystContent = (
    <div id="growth-calc-analyst-row" style={calcAnalystRowStyle}>
      {mode === 'page' && showBookmarkButton && (
        <div
          style={{
            position: 'absolute',
            right: '0.25rem',
            top: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            zIndex: 2,
          }}
        >
          {isBookmarkBusy && (
            <span
              style={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: isDarkMode ? '#9CA3AF' : '#6B7280',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Saving…
            </span>
          )}
          <button
            style={{
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              outline: 'none',
              boxShadow: 'none',
              opacity: isBookmarkBusy ? 0.6 : 1,
              transition: 'background-color 120ms ease',
            }}
            title="Bookmark"
            disabled={isBookmarkBusy}
            onClick={onBookmarkClick ?? (() => undefined)}
          >
            <img
              src={isBookmarked ? '/images/bookmark_fill.svg' : (isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg')}
              alt="Bookmark"
              style={{
                width: '20px',
                height: '20px',
              }}
            />
          </button>
        </div>
      )}
      {calcContent}
      {analystContent}
    </div>
  );

  const analystOnlyContent = (
    <div style={calcAnalystRowStyle}>
      {analystContent}
    </div>
  );

  const analystPanelContent = analystContent;

  const financialsContent = (
    <>

      {/* Financials (Annuals + Quarterly in one table) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${firstColWidthPx}px repeat(${annualIncomeCols.length}, 1fr) 1fr repeat(${quarterlyIncomeCols.length}, 1fr)`,
          marginBottom: 4,
        }}
      >
        <div style={{ fontWeight: 300 }}>Financials</div>
        <div style={{ fontWeight: 300, gridColumn: `2 / span ${annualIncomeCols.length}`, marginLeft: 2 }}>Annuals</div>
        <div style={{ gridColumn: `${2 + annualIncomeCols.length} / span 1` }} />
        <div
          style={{
            fontWeight: 300,
            gridColumn: `${3 + annualIncomeCols.length} / span ${quarterlyIncomeCols.length}`,
            marginLeft: 4,
          }}
        >
          Quarterly
        </div>
      </div>
      <table id="growth-financials-table" style={combinedTableStyle}>
        <thead>
          <tr>
            <th style={firstColHeaderStyle}>Fiscal Period</th>
            {annualIncomeCols.map((d, idx) => (
              <th
                style={
                  idx === annualIncomeCols.length - 1
                    ? {
                        ...thStyle,
                        borderRight: dividerBorder,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                    : {
                        ...thStyle,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                }
                key={`a-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
            <th style={{ ...thStyle, borderLeft: dividerBorder }}>TTM</th>
            {quarterlyIncomeCols.map((d) => (
              <th
                style={{
                  ...thStyle,
                  fontWeight: highlightedQuarterDate && d.date === highlightedQuarterDate ? 800 : thStyle.fontWeight,
                }}
                key={`q-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {['revenue', 'grossProfit', 'operatingIncome', 'netIncome', 'eps', 'operatingCashFlow'].map((field) => (
            <tr key={field}>
              <td style={firstColCellStyle}>
                {field === 'revenue' && 'Revenue'}
                {field === 'grossProfit' && 'Gross Profit'}
                {field === 'operatingIncome' && 'Operating Income'}
                {field === 'netIncome' && 'Net Income'}
                {field === 'eps' && 'EPS (Diluted)'}
                {field === 'operatingCashFlow' && 'Operating Cash Flow'}
              </td>

              {annualIncomeCols.map((d, idx) => {
                const cash = getCashByDate(d.date, annualCashCols);
                const value =
                  field === 'revenue'
                    ? d.revenue
                    : field === 'grossProfit'
                      ? d.grossProfit
                      : field === 'operatingIncome'
                        ? d.operatingIncome
                        : field === 'netIncome'
                          ? d.netIncome
                          : field === 'eps'
                            ? (d as any).epsDiluted ?? d.eps
                            : cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
                const cellStyle =
                  idx === annualIncomeCols.length - 1
                    ? { ...tdStyle, borderRight: dividerBorder, ...getNegativeNumberCellStyle(value) }
                    : { ...tdStyle, ...getNegativeNumberCellStyle(value) };
                const rendered = formatFinancialMetric(field as any, value);
                return (
                  <td style={cellStyle} key={`a-${d.date}`}>
                    {rendered}
                  </td>
                );
              })}

              {(() => {
                const ttmValue = getQuarterlyTTM(field as any);
                const cellStyle = { ...tdStyle, borderLeft: dividerBorder, ...getNegativeStringCellStyle(ttmValue) };
                return <td style={cellStyle}>{ttmValue}</td>;
              })()}

              {quarterlyIncomeCols.map((d) => {
                const cash = getCashByDate(d.date, quarterlyCashCols);
                const value =
                  field === 'revenue'
                    ? d.revenue
                    : field === 'grossProfit'
                      ? d.grossProfit
                      : field === 'operatingIncome'
                        ? d.operatingIncome
                        : field === 'netIncome'
                          ? d.netIncome
                          : field === 'eps'
                            ? (d as any).epsDiluted ?? d.eps
                            : cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
                const cellStyle = { ...tdStyle, ...getNegativeNumberCellStyle(value) };
                const rendered = formatFinancialMetric(field as any, value);
                return (
                  <td style={cellStyle} key={`q-${d.date}`}>
                    {rendered}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Profitability (Annuals + Quarterly in one table) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${firstColWidthPx}px repeat(${annualIncomeForProfit.length}, 1fr) 1fr repeat(${quarterlyIncomeForProfitCols.length}, 1fr)`,
          marginBottom: 4,
        }}
      >
        <div style={{ fontWeight: 300 }}>Profitability</div>
        <div style={{ fontWeight: 300, gridColumn: `2 / span ${annualIncomeForProfit.length}`, marginLeft: 2 }}>Annuals</div>
        <div style={{ gridColumn: `${2 + annualIncomeForProfit.length} / span 1` }} />
        <div
          style={{
            fontWeight: 300,
            gridColumn: `${3 + annualIncomeForProfit.length} / span ${quarterlyIncomeForProfitCols.length}`,
            marginLeft: 4,
          }}
        >
          Quarterly
        </div>
      </div>
      <table id="growth-profitability-table" style={combinedTableStyle}>
        <thead>
          <tr>
            <th style={firstColHeaderStyle}>Fiscal Period</th>
            {annualIncomeForProfit.map((d, idx) => (
              <th
                style={
                  idx === annualIncomeForProfit.length - 1
                    ? {
                        ...thStyle,
                        borderRight: dividerBorder,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                    : {
                        ...thStyle,
                        fontWeight: latestAnnualYear != null && new Date(d.date).getFullYear() === latestAnnualYear ? 800 : thStyle.fontWeight,
                      }
                }
                key={`a-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
            <th style={{ ...thStyle, borderLeft: dividerBorder }}>TTM</th>
            {quarterlyIncomeForProfitCols.map((d) => (
              <th
                style={{
                  ...thStyle,
                  fontWeight: highlightedQuarterDateForProfit && d.date === highlightedQuarterDateForProfit ? 800 : thStyle.fontWeight,
                }}
                key={`q-${d.date}`}
              >
                {formatDisplayPeriodLabel(d)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={firstColCellStyle}>Gross Profit Margin</td>
            {annualIncomeForProfit.map((d, idx) => {
              const pct = d.revenue ? (d.grossProfit / d.revenue) * 100 : undefined;
              const cellStyle =
                idx === annualIncomeForProfit.length - 1
                  ? { ...tdStyle, borderRight: dividerBorder, ...getNegativeNumberCellStyle(pct) }
                  : { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const v = getQuarterlyTTMMargin('gross');
              const cellStyle = { ...tdStyle, borderLeft: dividerBorder, ...getNegativeStringCellStyle(v) };
              return <td style={cellStyle}>{v}</td>;
            })()}
            {quarterlyIncomeForProfitCols.map((d) => {
              const pct = d.revenue ? (d.grossProfit / d.revenue) * 100 : undefined;
              const cellStyle = { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>Operating Income Margin</td>
            {annualIncomeForProfit.map((d, idx) => {
              const pct = d.revenue ? (d.operatingIncome / d.revenue) * 100 : undefined;
              const cellStyle =
                idx === annualIncomeForProfit.length - 1
                  ? { ...tdStyle, borderRight: dividerBorder, ...getNegativeNumberCellStyle(pct) }
                  : { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const v = getQuarterlyTTMMargin('operating');
              const cellStyle = { ...tdStyle, borderLeft: dividerBorder, ...getNegativeStringCellStyle(v) };
              return <td style={cellStyle}>{v}</td>;
            })()}
            {quarterlyIncomeForProfitCols.map((d) => {
              const pct = d.revenue ? (d.operatingIncome / d.revenue) * 100 : undefined;
              const cellStyle = { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>Net Income Margin</td>
            {annualIncomeForProfit.map((d, idx) => {
              const pct = d.revenue ? (d.netIncome / d.revenue) * 100 : undefined;
              const cellStyle =
                idx === annualIncomeForProfit.length - 1
                  ? { ...tdStyle, borderRight: dividerBorder, ...getNegativeNumberCellStyle(pct) }
                  : { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const v = getQuarterlyTTMMargin('net');
              const cellStyle = { ...tdStyle, borderLeft: dividerBorder, ...getNegativeStringCellStyle(v) };
              return <td style={cellStyle}>{v}</td>;
            })()}
            {quarterlyIncomeForProfitCols.map((d) => {
              const pct = d.revenue ? (d.netIncome / d.revenue) * 100 : undefined;
              const cellStyle = { ...tdStyle, ...getNegativeNumberCellStyle(pct) };
              const rendered = d.revenue ? formatPercentFromAlreadyPercent(pct, 1) : '-';
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>ROIC</td>
            {annualIncomeForProfit.map((d, idx) => {
              const v = getROICDisplay(d.date, 'annual');
              const cellStyle =
                idx === annualIncomeForProfit.length - 1
                  ? { ...tdStyle, borderRight: dividerBorder, ...getNegativeStringCellStyle(v) }
                  : { ...tdStyle, ...getNegativeStringCellStyle(v) };
              return (
                <td style={cellStyle} key={`a-${d.date}`}>
                  {v}
                </td>
              );
            })}
            {(() => {
              const v = typeof roicTtmSeries.latest === 'number' && Number.isFinite(roicTtmSeries.latest) ? formatPercent(roicTtmSeries.latest, 1) : '-';
              const cellStyle = { ...tdStyle, borderLeft: dividerBorder, ...getNegativeStringCellStyle(v) };
              return <td style={cellStyle}>{v}</td>;
            })()}
            {quarterlyIncomeForProfitCols.map((d) => {
              const v = getQuarterlyRoicAnnualizedDisplay(d.date);
              const cellStyle = { ...tdStyle, ...getNegativeStringCellStyle(v) };
              return (
                <td style={cellStyle} key={`q-${d.date}`}>
                  {v}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>OCF / Net Income</td>
            {annualIncomeForProfit.map((d, idx) => {
              const cash = getCashByDate(d.date, annualCashCols);
              const ocf = cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(ocf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle =
                idx === annualIncomeForProfit.length - 1
                  ? { ...tdStyle, borderRight: dividerBorder, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) }
                  : { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`a-ocf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const ratioDisplay = getQuarterlyTTMCashToNetIncomeRatio('ocf');
              const cellStyle = {
                ...tdStyle,
                borderLeft: dividerBorder,
                ...getNegativeStringCellStyle(ratioDisplay.value),
                ...(ratioDisplay.isNA ? { color: 'red' } : {}),
              };
              return <td style={cellStyle}>{ratioDisplay.value}</td>;
            })()}
            {quarterlyIncomeForProfitCols.map((d) => {
              const cash = getCashByDate(d.date, quarterlyCashCols);
              const ocf = cash?.netCashProvidedByOperatingActivities ?? cash?.operatingCashFlow;
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(ocf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle = { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`q-ocf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={firstColCellStyle}>FCF / Net Income</td>
            {annualIncomeForProfit.map((d, idx) => {
              const cash = getCashByDate(d.date, annualCashCols);
              const fcf = getCashFreeCashFlow(cash);
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(fcf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle =
                idx === annualIncomeForProfit.length - 1
                  ? { ...tdStyle, borderRight: dividerBorder, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) }
                  : { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`a-fcf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
            {(() => {
              const ratioDisplay = getQuarterlyTTMCashToNetIncomeRatio('fcf');
              const cellStyle = {
                ...tdStyle,
                borderLeft: dividerBorder,
                ...getNegativeStringCellStyle(ratioDisplay.value),
                ...(ratioDisplay.isNA ? { color: 'red' } : {}),
              };
              return <td style={cellStyle}>{ratioDisplay.value}</td>;
            })()}
            {quarterlyIncomeForProfitCols.map((d) => {
              const cash = getCashByDate(d.date, quarterlyCashCols);
              const fcf = getCashFreeCashFlow(cash);
              const ratioDisplay = formatCashToNetIncomeRatioDisplay(fcf, d.netIncome);
              const rendered = ratioDisplay.value;
              const cellStyle = { ...tdStyle, ...getNegativeStringCellStyle(rendered), ...(ratioDisplay.isNA ? { color: 'red' } : {}) };
              return (
                <td style={cellStyle} key={`q-fcf-ni-${d.date}`}>
                  {rendered}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </>
  );

  const bodyContent = (
    <>
      {sections === 'analyst-only'
        ? analystOnlyContent
        : sections === 'analyst-panel'
          ? analystPanelContent
          : calcAnalystContent}
      {sections === 'all' ? financialsContent : null}
    </>
  );

  const wrappedContent = useFrame ? (
    <div id={mode === 'page' ? 'growth-main-container' : 'growth-tables-embedded'} style={frameStyle}>
      {bodyContent}
    </div>
  ) : (
    bodyContent
  );

  return mode === 'page' ? (
    <div id="growth-main-frame" style={{ paddingTop: '15px' }}>
      {wrappedContent}
    </div>
  ) : (
    wrappedContent
  );
};

export default ResearchGrowthTables;
